import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/StyledButton/StyledButton';
import { ReduxState } from 'redux/types';

import CustomDialog from '../../components/CustomDialog/CustomDialog';
import { Props as CustomInputProps } from '../../components/CustomInput/CustomInput';
import Country from '../../types/Country';
import User, { EmptyUser } from '../../types/User';
import { UserRole } from '../../types/User';
import api from '../../utils/api';
import snackbar from '../../utils/snackbar';
import Footer from './components/Footer/Footer';
import Form from './components/Form/Form';

/*
 * This dialog contains the user details form.
 * It is used both to modify existing users and to create new ones.
 */

interface Props {
    open: boolean;
    onClose: (userId?: number) => void;
    userId: number;
    type?: UserRole;
    companyId?: number;
}

const UserDetailsDialog = (props: Props) => {
    const { open, onClose, userId, type, companyId } = props;

    const isAdmin = useSelector((state: ReduxState) => state.user.type) === 'admin';
    const isCertifier = useSelector((state: ReduxState) => state.user.type) === 'certifier';

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [userData, setUserData] = useState<User>(EmptyUser);
    const [serverUserData, setServerUserData] = useState<User>(EmptyUser);
    const [countries, setCountries] = useState<Country[]>([]);

    const loadData = useCallback(() => {
        // fetch user data
        setIsLoading(true);
        api.request('/users/' + userId).then((res) => {
            setUserData(res);
            setServerUserData(res);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [userId]);

    const handleAttributeChange = useCallback((attribute: string, value: any) => {
        setUserData(currentData => {
            return { ...currentData, [attribute]: value } as User;
        });
    }, []);

    const handleSubmit = useCallback((selectedCompanyId?: number | null) => {
        // called on form submit, send the data to the API
        setIsLoading(true);

        const requestData = { ...userData, ...(companyId && { companyId }), ...(selectedCompanyId !== undefined && { companyId: selectedCompanyId }) };

        api.request('/users/' + (userId === 0 ? '' : userId), (userId === 0 ? 'POST' : 'PUT'), requestData, false).then((res) => {
            if (selectedCompanyId) {
                api.request('/companies/' + companyId + '/users', 'POST', { userId: res.id }).then(() => {
                    const message = (userId === 0) ? t('dialogs.user_details.new_user_saved') : t('dialogs.user_details.user_saved');
                    snackbar.success(message);

                    onClose(res.id);
                });
            } else {
                const message = (userId === 0) ? t('dialogs.user_details.new_user_saved') : t('dialogs.user_details.user_saved');
                snackbar.success(message);

                onClose(res.id);
            }
        }).catch((res) => {
            snackbar.handleApiError(res);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [companyId, userData, userId, onClose, t]);

    const saveIfNeeded = useCallback(() => {
        // If the selected user type is different from the one saved on the server side, send the new type to the API.
        // This is needed before associate a company to a new designated auditor.
        if ((userData.type === serverUserData.type) || userId === 0) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            api.request('/users/' + userId, 'PUT', { ...serverUserData, type: userData.type }).then((res) => {
                setServerUserData(res);
                resolve();
            }).catch(() => {
                reject();
            });
        });

    }, [serverUserData, userData, userId]);

    const handleKeyDown = useCallback((e) => {
        // on enter key press, submit the form
        if (e.keyCode === 13 && !isLoading) {
            handleSubmit();
        }
    }, [handleSubmit, isLoading]);

    useEffect(() => {
        // fetch countries
        api.request('/countries').then((res) => {
            setCountries(res);
        });
    }, []);

    useEffect(() => {
        // call loadData if userId !== 0, so the user already exists
        if (!open) return;
        setUserData({ ...EmptyUser, ...(type && { type }) });

        if (userId !== 0) {
            loadData();
        }
    }, [loadData, open, type, userId]);

    const baseInputProps: Omit<CustomInputProps, 'name'> = {
        data: userData,
        disabled: isLoading,
        partialTranslationKey: 'models.user.attributes',
        handleAttributeChange,
        handleKeyDown,
        ...(!(isAdmin || (isCertifier && userId === 0)) && {
            // non-admin users cannot modify the data
            InputProps: {
                readOnly: true,
            }
        }),
    };

    return (
        <>
            <CustomDialog onClose={() => onClose()} open={open} fullWidth alwaysVisible>
                <DialogTitle className='dialog-title-text-ellipsis'>
                    {t('dialogs.user_details.title')}
                </DialogTitle>

                <DialogContent dividers>
                    <Form baseInputProps={baseInputProps} countries={countries} fixedType={type} />

                    {userData.id !== 0 && (
                        <Footer user={userData} updateData={loadData} saveIfNeeded={saveIfNeeded} isLoading={isLoading} />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => onClose()} color='secondary' disabled={isLoading} >
                        {t('dialogs.generic.close')}
                    </Button>

                    <Button onClick={() => handleSubmit()} color='primary' disabled={isLoading} >
                        {t('dialogs.generic.save')}
                    </Button>
                </DialogActions>
            </CustomDialog>
        </>
    );
};

export default UserDetailsDialog;
